/**
 * Choosing an animation that can actually reach the ball, and cheating just enough that it
 * looks like it did.
 *
 * The selector has already produced an ordered list of candidate clips. This walks that list
 * from the top and returns the FIRST one whose foot can plausibly be made to meet the ball —
 * there is no scoring here either, and no attempt to find the best. The order is the answer;
 * this only rejects.
 *
 * "Plausibly" is doing a lot of work. For each candidate the whole physics solver runs to
 * find out where the player would actually be at the contact frame, and the ball's own
 * forecast says where the ball will be. Those two rarely coincide. What decides the clip is
 * whether the gap is small enough to be hidden — and the gap that is then hidden, by nudging
 * the whole player sideways over the frames before contact, is the ACTION SMUGGLE.
 *
 * The smuggle is the single most important idea in how this game feels. A player never
 * misses a ball he was always going to reach, and never teleports either; he drifts a few
 * centimetres over a fifth of a second and the foot arrives. Part of the drift is then
 * deliberately thrown away again, because showing all of it makes the running look wrong —
 * the game would rather a player visibly miss by two centimetres than visibly slide.
 *
 * Source: humanoid.cpp:1859-1997 (`GetBodyBallDistanceAdvantage`), :1999-2323
 * (`GetBestCheatableAnimID`), humanoid_utils.cpp:103-115 (`GetFrontOfFootOffsetRel`).
 */

import { Vector3 } from 'three';
import { PITCH } from '../../core/constants';
import {
  absoluteAngle2D,
  angle2D,
  clamp,
  curve,
  fixAngle,
  modulateIntoRange,
  normalizedClamp,
  normalizedOr,
  normalizedOrZero,
  rotate2D,
  vecLength,
} from '../../core/math';
import { samplePosition, type AnimClip } from './clip';
import {
  calculateOutgoingMovement,
  calculatePhysicsVector,
  movementAtFrame,
  type AnimConfig,
  type PlayerPhysics,
} from './physics';
import { IDLE_VELOCITY, SPRINT_VELOCITY, Velocity, floatToEnumVelocity } from './velocity';
import type { SpatialState } from './spatial';

const FORWARD = new Vector3(0, -1, 0);

/** humanoid.cpp:43,47 — how much of the computed reach is actually granted. */
const CHEAT_FACTOR = 0.5;
const CHEAT_DISTANCE_BONUS = 0.02;
/** humanoid.cpp:48-49 — how much of the smuggle is thrown away rather than shown. */
const CHEAT_DISCARD_DISTANCE = 0.02;
const CHEAT_DISCARD_DISTANCE_MULTIPLIER = 0.4;
const MAX_SMUGGLE_DISCARD_DISTANCE = 0.2;
/** gamedefines.hpp:64,66 */
export const DEFAULT_TOUCH_OFFSET_MS = 80;
/**
 * The height every clip was drawn for, so a clip's ball height scales by `height / 1.92`.
 *
 * Exported because the AI needs the same figure to work out how high a ball a given man can
 * actually play — see `MatchPlayer.reachableBallHeight`.
 */
export const DEFAULT_PLAYER_HEIGHT = 1.92;

/**
 * Where the ball ought to sit relative to a player — `GetFrontOfFootOffsetRel`
 * (humanoid_utils.cpp:103-115).
 *
 * A little in front, further in front the faster he is going, and swung toward whichever way
 * his torso is turned — but only once he is moving fast enough for the torso to mean
 * anything. A high ball is drawn in closer to the body, because you cannot control a
 * bouncing ball at arm's length.
 */
export function frontOfFootOffsetRel(
  velocity: number,
  bodyAngleRel: number,
  height: number,
  out = new Vector3(),
): Vector3 {
  const distance = 0.34 + velocity * DEFAULT_TOUCH_OFFSET_MS * 0.001;
  const bodyAngle = velocity < 1.8 ? 0 : bodyAngleRel;
  rotate2D(new Vector3(0, -distance * 0.2, 0), bodyAngle, out);
  out.y += -distance * 0.8;
  return out.multiplyScalar(1 - clamp((height - 0.11) / 4, 0, 0.5));
}

/**
 * The same offset, swung round to follow the way the player is actually travelling at the
 * contact frame — humanoid.cpp:2192-2197.
 *
 * `frontOfFootOffsetRel` always comes back pointing straight ahead, because it is written in
 * a frame where forward is (0,-1,0). A player running sideways still wants the ball out
 * where he is going, so the whole offset is turned by the BEARING of his movement. Below
 * dribbling pace it is left alone; the original has an alternative commented out there and a
 * note that it does not really work either way.
 *
 * The rotation is `FixAngle(touchMovement.GetNormalized((0,-1,0)).GetAngle2D())`, and that
 * `GetAngle2D` is the NO-ARGUMENT one (vector3.hpp:276-280): a bearing measured from +X,
 * which `FixAngle` then converts to a bearing measured from forward. So it is
 * `fixAngle(absoluteAngle2D(...))` here, and NOT the two-vector `angle2D(movement, FORWARD)`
 * — that one already equals the whole expression, quarter turn included, so passing it
 * through `fixAngle` turns the offset a further 90 degrees and the reach test then probes
 * beside the player instead of in front of him — the region it allows him shrinks by about a
 * fifth on an ordinary touch in front. `docs/original-engine/verified/cross-check.md`, sixth
 * resolved item, warns against exactly this substitution wherever the original takes a
 * bearing.
 */
export function touchFrontOfFootOffset(
  touchVelocity: number,
  ballHeight: number,
  touchMovement: Vector3,
  out = new Vector3(),
): Vector3 {
  // The torso angle handed to the offset is read off the clip's ROOT keyframe, and root
  // keyframes carry position only — the orientation beside them is always the identity
  // (animation.cpp:1083-1102). So it is always zero, and the offset's angled component never
  // actually swings. Passed explicitly rather than dropped, because it is a real parameter
  // that a rewrite could legitimately feed.
  frontOfFootOffsetRel(touchVelocity, 0, ballHeight, out);
  if (floatToEnumVelocity(touchVelocity) === Velocity.Idle) return out;
  const bearing = absoluteAngle2D(normalizedOr(touchMovement, FORWARD, _touchDir));
  return rotate2D(out, fixAngle(bearing), out);
}

const _touchDir = new Vector3();

export interface ReachInputs {
  functionType: string;
  animTouchMovement: Vector3;
  touchMovement: Vector3;
  incomingMovement: Vector3;
  outgoingMovement: Vector3;
  outgoingAngle: number;
  bodyPos: Vector3;
  frontOfFoot: Vector3;
  animBallPos2D: Vector3;
  actualBallPos2D: Vector3;
  radiusFactor: number;
  radiusCheatDistance: number;
}

/**
 * Can this clip's contact be made to happen — `GetBodyBallDistanceAdvantage`
 * (humanoid.cpp:1859-1997).
 *
 * Despite the name it answers yes or no. It builds a region around where the clip WANTS the
 * ball to be, and asks whether the ball will actually be inside it. Three things shape that
 * region, and all three are about making the answer feel fair rather than be geometric:
 *
 *   - it grows when the ball is already near the player's feet, and when he is moving
 *     slowly — a jogging player has time to adjust, a sprinter does not;
 *   - it is offset BACKWARDS along the direction he is about to travel, because a ball
 *     behind you as you accelerate away is much easier to reach than one the same distance
 *     ahead;
 *   - it is squashed into an ellipse at speed, narrow across the direction of travel, so a
 *     sprinting player can stretch forwards for a ball but not sideways.
 */
export function bodyBallDistanceAdvantage(input: ReachInputs): boolean {
  const outgoingVelocity = vecLength(input.outgoingMovement);
  const averageInOutVelocity =
    vecLength(_sum.copy(input.incomingMovement).add(input.outgoingMovement)) * 0.5;

  normalizedOrZero(input.frontOfFoot, _ffoDir);
  const bodyPlusFfo = _bodyPlusFfo.copy(input.bodyPos).addScaledVector(_ffoDir, 0.1);

  const bodyAnimBallBonus =
    1 - curve(normalizedClamp(bodyPlusFfo.distanceTo(input.animBallPos2D), 0, 0.7), 0.7);
  const bodyActualBallBonus =
    1 - curve(normalizedClamp(bodyPlusFfo.distanceTo(input.actualBallPos2D), 0, 0.7), 0.4);
  const velocityBonus = 1 - normalizedClamp(averageInOutVelocity, IDLE_VELOCITY, SPRINT_VELOCITY);

  let radius = input.radiusFactor;
  radius *=
    1 +
    1.0 * bodyAnimBallBonus +
    0.6 * bodyActualBallBonus +
    0.8 * bodyAnimBallBonus * bodyActualBallBonus +
    1.0 * velocityBonus;

  const outgoingDirection =
    floatToEnumVelocity(outgoingVelocity) === Velocity.Idle
      ? rotate2D(FORWARD, input.outgoingAngle, _outDir)
      : normalizedOrZero(input.outgoingMovement, _outDir);

  // Where "behind" is: mostly the direction he is about to leave in, with a little of where
  // he came from and where his foot is at contact.
  const behindUnscaled = _behindUnscaled
    .copy(input.incomingMovement)
    .multiplyScalar(0.1)
    .addScaledVector(input.touchMovement, 0.2)
    .addScaledVector(input.outgoingMovement, 0.7)
    .negate();
  const behindVector = normalizedOrZero(behindUnscaled, _behind).multiplyScalar(
    Math.pow(normalizedClamp(vecLength(behindUnscaled), 0, SPRINT_VELOCITY), 0.5),
  );
  // Cornering gets a more centred region, so a turn has a richer range of reachable balls.
  const dot = 0.5 + clamp(FORWARD.dot(outgoingDirection) * 0.5 + 0.5, 0, 1) * 0.5;
  behindVector.multiplyScalar(dot);

  const animToActualBall = _animToActual.copy(input.actualBallPos2D).sub(input.animBallPos2D);

  // Squash the region across the direction of travel, keeping its area the same.
  const straightAngleVector = normalizedOr(input.incomingMovement, outgoingDirection, _straight);
  const toStraightAngle = angle2D(FORWARD, straightAngleVector);
  rotate2D(animToActualBall, toStraightAngle, animToActualBall);
  const lateralRadiusFactor =
    0.6 -
    0.3 *
      Math.pow(
        normalizedClamp(vecLength(input.incomingMovement), IDLE_VELOCITY, SPRINT_VELOCITY),
        0.7,
      );
  animToActualBall.x /= lateralRadiusFactor;
  radius *= Math.pow(1 / lateralRadiusFactor, 0.5);
  rotate2D(animToActualBall, -toStraightAngle, animToActualBall);

  const adaptedActualBall = _adapted.copy(input.animBallPos2D).add(animToActualBall);
  const behindCenter = _center
    .copy(input.animBallPos2D)
    .addScaledVector(
      behindVector,
      (radius + input.radiusCheatDistance * 0.6) * CHEAT_FACTOR,
    );
  const allowedRadius = (radius + input.radiusCheatDistance) * CHEAT_FACTOR + CHEAT_DISTANCE_BONUS;

  return adaptedActualBall.distanceTo(behindCenter) <= allowedRadius;
}

const _sum = new Vector3();
const _ffoDir = new Vector3();
const _bodyPlusFfo = new Vector3();
const _outDir = new Vector3();
const _behindUnscaled = new Vector3();
const _behind = new Vector3();
const _animToActual = new Vector3();
const _straight = new Vector3();
const _adapted = new Vector3();
const _center = new Vector3();

/** Everything the search needs that is not a candidate clip. */
export interface TouchSearchContext {
  library: AnimClip[];
  spatial: SpatialState;
  player: PlayerPhysics;
  config: AnimConfig;
  /** Standing height in metres; the clip's ball height is scaled by it. */
  playerHeight: number;
  /** Where this player believes the ball will be, `timeMs` from now. */
  ballPrediction: (timeMs: number, out?: Vector3) => Vector3;
  /** The true forecast, used only for the in-play bounds check. */
  ballPredictionTrue: (timeMs: number, out?: Vector3) => Vector3;
  /** 1 just after this player touched the ball, fading over the given window. */
  lastTouchBias: (decayMs: number, atMs: number) => number;
  nowMs: number;
  closestOpponentDistance: number;
  /** Set when this player has the ball in his hands; every gate below relaxes. */
  isBallRetainer: boolean;
  /** True when someone ELSE is holding the ball, which forbids all contact. */
  someoneElseRetainsBall: boolean;
  /** True when a pass or shot is also on the queue, which makes traps less attractive. */
  preferPassAndShot: boolean;
}

export interface TouchSelection {
  clipIndex: number;
  touchFrame: number;
  /** The re-integrated path for the chosen clip, relative to the start position. */
  positions: Vector3[];
  rotationOffset: number;
  /** Where the ball will be at contact — world space, with height. */
  touchPos: Vector3;
  /** The whole gap that has to be closed, before any of it is thrown away. */
  fullActionSmuggle: Vector3;
  /** The part of it the player will actually be moved by. */
  actionSmuggle: Vector3;
}

/**
 * Walk the candidates and take the first that can reach the ball —
 * `GetBestCheatableAnimID` (humanoid.cpp:1999-2323).
 *
 * Returns null when none of them can, which is a perfectly ordinary outcome: it means this
 * player is not getting to this ball, and the next command in his queue gets a turn.
 */
export function findReachableTouch(
  context: TouchSearchContext,
  dataSet: number[],
  useDesiredMovement: boolean,
  desiredDirection: Vector3,
  desiredVelocityFloat: number,
  desiredBodyDirectionRel: Vector3,
): TouchSelection | null {
  if (context.someoneElseRetainsBall) return null;
  if (dataSet.length === 0) return null;

  const { spatial, library } = context;
  const incomingMovement = rotate2D(spatial.movement, -spatial.angle);
  const desiredMovement = desiredDirection.clone().multiplyScalar(desiredVelocityFloat);
  // Every candidate in the set shares a function type — the crude filter saw to that — so
  // the original reads it once, off the head of the list, and so does this.
  const functionType = library[dataSet[0]!]!.animType;

  for (const clipIndex of dataSet) {
    const clip = library[clipIndex]!;
    const isBase = clip.vars['baseanim'] === 'true';

    const solved = calculatePhysicsVector(
      clip,
      useDesiredMovement,
      desiredMovement,
      desiredBodyDirectionRel,
      spatial,
      context.player,
      context.config,
    );
    const positions = solved.positions;

    const predictedAngle = modulateIntoRange(
      -Math.PI,
      Math.PI,
      clip.outgoingAngle + solved.rotationOffset,
    );
    const outgoingMovement = rotate2D(calculateOutgoingMovement(positions), -spatial.angle);

    // Try the middle contact first, then outwards. With one contact per clip — which is all
    // the shipped data has — this is just "try it".
    for (const touchIndex of middleOutOrder(clip.touches.length)) {
      const touch = clip.touches[touchIndex]!;
      const animTouchFrame = touch.frame;
      if (animTouchFrame >= positions.length) continue;

      // Will the ball still be on the pitch when the foot gets there?
      if (!context.isBallRetainer) {
        const absBallPos = context.ballPredictionTrue(animTouchFrame * 10, _absBall);
        if (
          Math.abs(absBallPos.x) > PITCH.halfLength + PITCH.lineHalfWidth + 0.11 ||
          Math.abs(absBallPos.y) > PITCH.halfWidth + PITCH.lineHalfWidth + 0.11
        ) {
          continue;
        }
      }

      // Everything from here is in the player's own frame: he is at the origin, facing -Y.
      const touchMovement = rotate2D(movementAtFrame(positions, animTouchFrame), -spatial.angle);
      const animTouchMovement = movementAtFrame(clip.positionCache, animTouchFrame);

      const ballPos = context
        .ballPrediction(animTouchFrame * 10, _ballPos)
        .sub(spatial.position);
      rotate2D(ballPos, -spatial.angle, ballPos);
      const ballMovement = context
        .ballPrediction(animTouchFrame * 10 + 10, _ballMovement)
        .sub(context.ballPrediction(animTouchFrame * 10, _ballPrev))
        .multiplyScalar(100);
      rotate2D(ballMovement, -spatial.angle, ballMovement);

      const bodyPos = rotate2D(positions[animTouchFrame]!, -spatial.angle, _bodyPos);
      bodyPos.z = 0;

      // Where the clip wants the ball, moved from the clip's own path onto the solved one,
      // and scaled for how tall this particular player is.
      const animBodyPos = samplePosition(clip.root, clip.frameCount, animTouchFrame, _animBodyPos);
      const animBallHeight = touch.position.z;
      const animBallPos = _animBallPos.copy(touch.position).sub(animBodyPos);
      animBallPos.add(rotate2D(positions[animTouchFrame]!, -spatial.angle, _scratch));
      animBallPos.z = animBallHeight * (context.playerHeight / DEFAULT_PLAYER_HEIGHT);

      // The forecast is a series of points, but the ball is really a continuum. Slide it
      // along the 9 ms of flight around the contact and take the closest approach, so a
      // fast ball is not unfairly judged on where it happened to be sampled.
      slideBallAlongItsPath(ballPos, ballMovement, animBallPos);

      const smuggle3D = _smuggle3D.copy(ballPos).sub(animBallPos);

      // --- can he get his foot to the right HEIGHT? ---
      let ballDistanceZ = Math.abs(smuggle3D.z);
      // High balls are allowed more vertical cheating, or the library would need a clip for
      // every height there is.
      ballDistanceZ *= 1 - clamp((animBallPos.z - 0.11) * 0.3, 0, 0.2);
      ballDistanceZ *= 1 - clamp((ballPos.z - 0.11) * 0.4, 0, 0.3);
      if (ballPos.z > 1.8 && ballPos.z > animBallPos.z + 0.12) ballDistanceZ *= 2;
      if (ballPos.z > 2.6 && ballPos.z > animBallPos.z + 0.08) ballDistanceZ *= 20;
      if (functionType === 'deflect') ballDistanceZ *= 0.8;
      if (context.isBallRetainer) ballDistanceZ = 0;
      // A ground-level clip may be stretched up to a low ball; it does not look too bad.
      if (ballPos.z < 0.5 && isBase) ballDistanceZ = Math.max(ballDistanceZ - 0.15, 0);
      if (ballDistanceZ >= 0.22) continue;

      // --- can he get his foot to the right PLACE? ---
      // A contact away from the clip's natural moment is a stretch, and a stretch is
      // granted less reach.
      const touchFrameAwkwardness =
        Math.pow(normalizedClamp(Math.abs(clip.touchFrame - animTouchFrame), 0, 4), 2) * 0.5;

      // Clips that touch the ball early are favoured: they cheat just as much, but for
      // less time, so less of it is visible.
      let touchFrameFactor = Math.pow(animTouchFrame / 24, 0.7);
      if (touchFrameFactor > 1) touchFrameFactor = 1 + (touchFrameFactor - 1) * 0.5;

      let radiusFactor = 0.3 * (1 - touchFrameAwkwardness);
      let radiusCheatOffset = 0;
      switch (functionType) {
        case 'deflect':
          radiusFactor *= 1.8;
          radiusCheatOffset += 0.4;
          break;
        case 'sliding':
          // Deliberately tiny: a slide that misses the ball is usually the better slide.
          radiusFactor *= 0.2;
          radiusCheatOffset = 0;
          break;
        case 'interfere':
          radiusFactor *= 1.4;
          radiusCheatOffset += 0.2;
          break;
        case 'shortpass':
        case 'longpass':
        case 'highpass':
        case 'shot':
          radiusFactor *= 1.3;
          radiusCheatOffset += 0.15;
          break;
        default:
          break;
      }
      if ((functionType === 'trap' || functionType === 'ballcontrol') && context.preferPassAndShot) {
        radiusFactor *= 0.3;
      }
      radiusCheatOffset *= 1 - touchFrameAwkwardness;

      const touchVelo = vecLength(touchMovement);
      const frontOfFoot = touchFrontOfFootOffset(touchVelo, ballPos.z, touchMovement, _ffo);

      // He has only just touched it: he cannot touch it again yet, however close it is.
      const lastTouchBias = curve(
        context.lastTouchBias(600, context.nowMs + animTouchFrame * 10),
        1.0,
      );
      if (lastTouchBias > 0) {
        const factor = 1 - lastTouchBias * 0.97 * (1 - context.player.stat('technical_ballcontrol') * 0.1);
        radiusFactor *= factor;
        radiusCheatOffset *= factor;
      }

      const reachable = bodyBallDistanceAdvantage({
        functionType,
        animTouchMovement,
        touchMovement,
        incomingMovement,
        outgoingMovement,
        outgoingAngle: predictedAngle,
        bodyPos,
        frontOfFoot,
        animBallPos2D: _flat2D.set(animBallPos.x, animBallPos.y, 0),
        actualBallPos2D: _flatBall.set(ballPos.x, ballPos.y, 0),
        radiusFactor: radiusFactor * touchFrameFactor,
        radiusCheatDistance: radiusCheatOffset,
      });

      if (!reachable && !context.isBallRetainer) continue;

      // Found one.
      const touchPos = context.ballPrediction(animTouchFrame * 10, new Vector3());
      const fullActionSmuggle = rotate2D(
        new Vector3(smuggle3D.x, smuggle3D.y, 0),
        spatial.angle,
      );
      const actionSmuggle = discardSmuggle(
        context,
        fullActionSmuggle,
        functionType,
        touchPos,
        animTouchFrame,
        outgoingMovement,
        spatial.angle + predictedAngle,
      );

      return {
        clipIndex,
        touchFrame: animTouchFrame,
        positions,
        rotationOffset: solved.rotationOffset,
        touchPos,
        fullActionSmuggle,
        actionSmuggle,
      };
    }
  }

  return null;
}

/**
 * Throw away part of the smuggle — humanoid.cpp:2244-2317.
 *
 * A trade, made explicitly: showing the whole nudge makes the foot meet the ball exactly but
 * makes the running look like skating, and showing none of it makes the running perfect but
 * the foot miss. The original keeps a bit over half of it, and much less of it forwards,
 * because a player sliding FORWARDS is far more obvious than one sliding sideways.
 */
function discardSmuggle(
  context: TouchSearchContext,
  fullActionSmuggle: Vector3,
  functionType: string,
  touchPos: Vector3,
  animTouchFrame: number,
  outgoingMovement: Vector3,
  toStraightAngle: number,
): Vector3 {
  const actionSmuggle = fullActionSmuggle.clone();
  const fullLength = vecLength(actionSmuggle);

  let multiplier = CHEAT_DISCARD_DISTANCE_MULTIPLIER;
  if (functionType !== 'ballcontrol') multiplier *= 0.8;
  if (touchPos.z > 0.5) multiplier *= 0.7;
  if (touchPos.z > 1.0) multiplier *= 0.7;
  // Interference is a scramble; breaking its flow to be tidy is worse than the cheat.
  const bonus = functionType === 'interfere' ? 0.1 : 0;

  let smuggleDistance = clamp(fullLength - (CHEAT_DISCARD_DISTANCE + bonus), 0, 100);
  smuggleDistance *= 1 - multiplier;
  smuggleDistance = Math.min(smuggleDistance, fullLength - MAX_SMUGGLE_DISCARD_DISTANCE);
  if (context.isBallRetainer) smuggleDistance = 0;
  normalizedOrZero(actionSmuggle, actionSmuggle).multiplyScalar(smuggleDistance);

  // Forward creep is the visible kind, so it is capped hard, and the cap tightens the
  // faster he is going and the sooner the contact comes.
  rotate2D(actionSmuggle, -toStraightAngle, actionSmuggle);
  const allowForward =
    0.25 *
    (1 -
      Math.pow(
        normalizedClamp(vecLength(outgoingMovement), IDLE_VELOCITY, SPRINT_VELOCITY - 2.0),
        0.6,
      )) *
    (animTouchFrame * 0.1);
  // A pass or a shot does not have to run anywhere afterwards, so it may creep further.
  const extra = functionType !== 'ballcontrol' && functionType !== 'trap' ? 0.1 : 0;
  if (actionSmuggle.y < 0) {
    actionSmuggle.y = clamp(actionSmuggle.y + 0.02, -(allowForward + extra), 0);
  }
  rotate2D(actionSmuggle, toStraightAngle, actionSmuggle);

  // Less cheating when an opponent is close enough to see it.
  actionSmuggle.multiplyScalar(
    0.7 + 0.3 * normalizedClamp(context.closestOpponentDistance, 0.6, 1.2),
  );
  actionSmuggle.z = 0;
  return actionSmuggle;
}

/**
 * Slide the ball along the 9 ms of its path around the contact, to the point closest to
 * where the clip wants it (humanoid.cpp:2121-2126).
 */
function slideBallAlongItsPath(ballPos: Vector3, ballMovement: Vector3, animBallPos: Vector3): void {
  const from = _lineFrom.copy(ballPos).addScaledVector(ballMovement, -0.006);
  const to = _lineTo.copy(ballPos).addScaledVector(ballMovement, 0.003);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSq = dx * dx + dy * dy + (to.z - from.z) * (to.z - from.z);
  if (lengthSq < 1e-12) {
    ballPos.copy(from);
    return;
  }
  // The original projects in 2D but divides by the 3D length, which makes a rising ball's
  // slide shorter than a flat one's. Reproduced.
  const u = clamp(((animBallPos.x - from.x) * dx + (animBallPos.y - from.y) * dy) / lengthSq, 0, 1);
  ballPos.set(
    from.x + (to.x - from.x) * u,
    from.y + (to.y - from.y) * u,
    from.z + (to.z - from.z) * u,
  );
}

/** humanoid.cpp:2064-2073 — the middle contact first, then out to both ends. */
export function middleOutOrder(count: number): number[] {
  const order: number[] = [];
  for (let i = Math.floor(count / 2); i > -1; i--) order.push(i);
  for (let i = Math.floor(count / 2) + 1; i < count; i++) order.push(i);
  return order;
}

const _absBall = new Vector3();
const _ballPos = new Vector3();
const _ballPrev = new Vector3();
const _ballMovement = new Vector3();
const _bodyPos = new Vector3();
const _animBodyPos = new Vector3();
const _animBallPos = new Vector3();
const _smuggle3D = new Vector3();
const _ffo = new Vector3();
const _scratch = new Vector3();
const _flat2D = new Vector3();
const _flatBall = new Vector3();
const _lineFrom = new Vector3();
const _lineTo = new Vector3();
